export interface VirtualRow<TItem, TRow> {
	key: number
	item: TItem
	row: TRow | undefined
	// True when no rendered slot at or after this one has a row. Those slots sit
	// past the end of the list (the count can outrun the loaded rows, since it
	// keeps a high-water mark), so they stay blank instead of showing a
	// placeholder that would never resolve.
	isTrailing: boolean
	// True when no rendered slot at or before this one has a row. On a settled
	// page those slots sit above the loaded window and never resolve either.
	isLeading: boolean
}

// zero-virtual keys virtual items by row id. A row that sits in two windows at
// once (the permalink anchor slot plus a neighbour page whose sort cursor moved)
// hands React the same key twice, and React then drops one of the two rows,
// leaving an empty slot in the list. Keying by index and rendering a row at most
// once keeps every slot accounted for.
export const toVirtualRows = <
	TRow extends { id: string },
	TItem extends { index: number },
>(
	virtualItems: readonly TItem[],
	rowAt: (index: number) => TRow | undefined,
): VirtualRow<TItem, TRow>[] => {
	const seen = new Set<string>()

	const rows = virtualItems.map((item) => {
		const row = rowAt(item.index)
		const isDuplicate = row !== undefined && seen.has(row.id)
		if (row && !isDuplicate) {
			seen.add(row.id)
		}

		return {
			key: item.index,
			item,
			row: isDuplicate ? undefined : row,
			isTrailing: false,
			isLeading: false,
		}
	})

	for (let i = rows.length - 1; i >= 0; i--) {
		const entry = rows[i]
		if (!entry || entry.row) break
		entry.isTrailing = true
	}

	for (const entry of rows) {
		if (entry.row) break
		entry.isLeading = true
	}

	return rows
}
